// El modelo de una regla del Centro de Alertas, sin React: el borrador que edita el constructor, su
// traducción al `RuleInput` del contrato (§2), la frase legible (la MISMA para el constructor y para
// la fila de «Mis reglas»), la validación local y los filtros de la lista.
//
// La frase sale de la estructura y nunca al revés: el constructor no parsea texto. Así la fila de la
// lista y los chips del constructor no pueden decir dos cosas distintas de la misma regla.
import {getValueTypeBadge} from '../dashboard/metric_groups';

export const CONDITION_ABSOLUTE = 'ABSOLUTE_THRESHOLD';
// v2 (contrato v2 §2): «sube / cae / se aleja más de un N % contra el período anterior», y la fila de un
// aviso de control, que no tiene número (sólo guarda a quién y por dónde).
export const CONDITION_PERIOD_CHANGE = 'PERIOD_CHANGE';
export const CONDITION_CONTROL = 'CONTROL';
// ROAS y ACOS de Dashbo contra el mes pasado comparan la columna fija del Tablero (contrato v2 §2): su período
// y el de comparación no se eligen.
export const VS_SOURCE_FIXED_COLUMN = 'FIXED_COLUMN';

export const OP_LT = 'LT';
export const OP_LTE = 'LTE';
export const OP_GT = 'GT';
export const OP_GTE = 'GTE';
export const OPERATORS = [OP_LT, OP_LTE, OP_GT, OP_GTE];
export const OP_RISES = 'RISES_MORE_THAN';
export const OP_FALLS = 'FALLS_MORE_THAN';
export const OP_MOVES = 'MOVES_MORE_THAN';
export const PERIOD_CHANGE_OPERATORS = [OP_RISES, OP_FALLS, OP_MOVES];

export const SCOPE_CLIENT = 'CLIENT';
export const SCOPE_LINE = 'LINE';
export const SCOPE_LABEL = 'LINES_WITH_LABEL';
export const SCOPE_ANY = 'ANY_LINE';
export const SCOPE_TYPES = [SCOPE_CLIENT, SCOPE_LINE, SCOPE_LABEL, SCOPE_ANY];
// Todas las líneas activas de clientes activos, sin creador (las de Dashbo; también un admin, si
// `options.scopes` lo trae). Como «cualquier línea», no elige ids.
export const SCOPE_AGENCY = 'AGENCY';
const KNOWN_SCOPE_TYPES = [...SCOPE_TYPES, SCOPE_AGENCY];

export const RECIPIENTS_ME = 'ME';
export const RECIPIENTS_ASSIGNED = 'ASSIGNED';
export const RECIPIENTS_USERS = 'USERS';
export const RECIPIENTS_ALL = 'ALL_AGENCY';
export const RECIPIENT_TYPES = [RECIPIENTS_ME, RECIPIENTS_ASSIGNED, RECIPIENTS_USERS, RECIPIENTS_ALL];
export const RECIPIENTS_ADMINS = 'ADMINS';
// Las reglas de equipo (ronda 5, D19): las únicas con baja propia y con «salvo …».
export const TEAM_RECIPIENT_TYPES = [RECIPIENTS_ASSIGNED, RECIPIENTS_ALL, RECIPIENTS_ADMINS];
const KNOWN_RECIPIENT_TYPES = [...RECIPIENT_TYPES, RECIPIENTS_ADMINS];

export const CHANNEL_EMAIL = 'EMAIL';
export const CHANNEL_WEBHOOK = 'WEBHOOK';
// Las tres combinaciones que ofrece el chip del canal, en el orden del menú.
export const CHANNEL_CHOICES = [[CHANNEL_EMAIL], [CHANNEL_WEBHOOK], [CHANNEL_EMAIL, CHANNEL_WEBHOOK]];

export const UNIT_NUMBER = 'NUMBER';
export const UNIT_PERCENTAGE = 'PERCENTAGE';
export const UNIT_CURRENCY = 'CURRENCY';

export const KIND_CUSTOM = 'CUSTOM';
export const KIND_SYSTEM = 'SYSTEM';

export const DEFAULT_PERIOD = 'THIS_PERIOD';
// Los períodos que son un día: en la frase van sin «en» («… por encima de $500 ayer»).
const DAY_PERIODS = ['TODAY', 'YESTERDAY'];

// Los topes que valida el backend en el command (README §2.3). Se espejan acá para avisar antes de
// mandar; el backend sigue siendo el que decide (`INVALID_*` / `LIMIT_REACHED`).
export const MAX_RECIPIENTS = 20;
export const MAX_SCOPE_IDS = 30;
export const NAME_MAX_LENGTH = 80;

const OPERATOR_KEYS = {
    [OP_LT]: 'alerts_center.builder.op_lt',
    [OP_LTE]: 'alerts_center.builder.op_lte',
    [OP_GT]: 'alerts_center.builder.op_gt',
    [OP_GTE]: 'alerts_center.builder.op_gte',
    [OP_RISES]: 'alerts_center.builder.op_rises',
    [OP_FALLS]: 'alerts_center.builder.op_falls',
    [OP_MOVES]: 'alerts_center.builder.op_moves',
};

const RECIPIENT_KEYS = {
    [RECIPIENTS_ME]: 'alerts_center.builder.recipient_me',
    [RECIPIENTS_ASSIGNED]: 'alerts_center.builder.recipient_assigned',
    [RECIPIENTS_USERS]: 'alerts_center.builder.recipient_users',
    [RECIPIENTS_ALL]: 'alerts_center.builder.recipient_all',
    [RECIPIENTS_ADMINS]: 'alerts_center.builder.recipient_admins',
};

const SCOPE_KEYS = {
    [SCOPE_CLIENT]: 'alerts_center.builder.scope_client',
    [SCOPE_LINE]: 'alerts_center.builder.scope_line',
    [SCOPE_LABEL]: 'alerts_center.builder.scope_label',
    [SCOPE_ANY]: 'alerts_center.builder.scope_any',
    [SCOPE_AGENCY]: 'alerts_center.builder.scope_agency',
};

/** ¿El alcance elige ids? «Cualquier línea» y «toda la agencia», no. */
export function scopeHasIds(type) {
    return type !== SCOPE_ANY && type !== SCOPE_AGENCY;
}

// Lo que `options` ofrece de más (contrato v2 §6), como lista de ids o de `{id}`.
function optionIds(list) {
    return new Set((Array.isArray(list) ? list : []).map((item) => (item && typeof item === 'object' ? item.id : item)));
}

/** Los alcances del chip: los de siempre, y «toda la agencia» si `options.scopes` lo trae. */
export function scopeTypesFor(options) {
    return optionIds(options && options.scopes).has(SCOPE_AGENCY) ? [...SCOPE_TYPES, SCOPE_AGENCY] : SCOPE_TYPES;
}

/** Los destinatarios del chip: los de siempre, y «los administradores» si `options.recipients` lo trae. */
export function recipientTypesFor(options) {
    return optionIds(options && options.recipients).has(RECIPIENTS_ADMINS)
        ? [...RECIPIENT_TYPES, RECIPIENTS_ADMINS] : RECIPIENT_TYPES;
}

/** Los operadores de cada tipo de condición. */
export function operatorsFor(conditionType) {
    return conditionType === CONDITION_PERIOD_CHANGE ? PERIOD_CHANGE_OPERATORS : OPERATORS;
}

export function conditionTypeOfOperator(operator) {
    return PERIOD_CHANGE_OPERATORS.includes(operator) ? CONDITION_PERIOD_CHANGE : CONDITION_ABSOLUTE;
}

// Las métricas derivadas de la línea (`LINE:*`, contrato v2 §6): período fijo, unidad %.
export const LINE_METRIC_PREFIX = 'LINE:';

export function isLineMetric(metric) {
    return String(metric || '').startsWith(LINE_METRIC_PREFIX);
}

const SCOPE_PICK_KEYS = {
    [SCOPE_CLIENT]: 'alerts_center.builder.pick_client',
    [SCOPE_LINE]: 'alerts_center.builder.pick_line',
    [SCOPE_LABEL]: 'alerts_center.builder.pick_label',
};

export function operatorKey(operator) {
    return OPERATOR_KEYS[operator] || OPERATOR_KEYS[OP_LT];
}

export function recipientKey(type) {
    return RECIPIENT_KEYS[type] || RECIPIENT_KEYS[RECIPIENTS_ME];
}

export function scopeKey(type) {
    return SCOPE_KEYS[type] || SCOPE_KEYS[SCOPE_CLIENT];
}

export function channelsKey(channels) {
    const list = channels || [];
    const email = list.includes(CHANNEL_EMAIL);
    const webhook = list.includes(CHANNEL_WEBHOOK);
    if (email && webhook) return 'alerts_center.builder.channel_both';
    if (webhook) return 'alerts_center.builder.channel_webhook';
    return 'alerts_center.builder.channel_email';
}

export function sameChannels(a, b) {
    const left = [...(a || [])].sort().join(',');
    const right = [...(b || [])].sort().join(',');
    return left === right;
}

// --------------------------------------------------------------------------- borrador -----------

export function emptyDraft() {
    return {
        channels: [CHANNEL_EMAIL],
        recipients: {type: RECIPIENTS_ME, userIds: []},
        scope: {type: SCOPE_CLIENT, ids: []},
        metric: null,
        // `ABSOLUTE_THRESHOLD` o `PERIOD_CHANGE`: lo decide el operador que se elige en la frase.
        conditionType: CONDITION_ABSOLUTE,
        operator: OP_LT,
        value: '',
        period: DEFAULT_PERIOD,
        // Sólo en `PERIOD_CHANGE`: contra qué período se compara.
        vsPeriod: null,
        name: '',
        // Mientras el usuario no toque el nombre, se propone solo a partir de la frase.
        nameTouched: false,
        isActive: true,
    };
}

// ------------------------------------------------------------------------ plantillas ------------

/**
 * Las plantillas de «Crear una alerta» (recorrido con Pablo, 13/10, C-06): frases armadas SÓLO con lo
 * que el constructor admite hoy (métrica, comparador y período del catálogo; umbral absoluto). El valor
 * queda vacío: cada agencia sabe cuál es su número.
 *
 * Van «a mí» y no «a los asignados»: los asignados son quienes tienen el cliente asignado a mano
 * (`AlertRuleRecipientsResolver`), y el admin de una agencia recién creada no tiene ninguno, así que «a
 * los asignados» no le llegaría a nadie. El chip se cambia en un clic.
 */
export const RULE_TEMPLATES = [
    {key: 'cpa_high', metric: 'CPA', operator: OP_GT, period: 'LAST_7_DAYS'},
    {key: 'roas_low', metric: 'ROAS', operator: OP_LT, period: 'LAST_7_DAYS'},
    {key: 'ctr_low', metric: 'CTR', operator: OP_LT, period: 'LAST_7_DAYS'},
    {key: 'cost_high', metric: 'Cost', operator: OP_GT, period: 'YESTERDAY'},
];

/** El borrador de una plantilla: por correo, a mí, en cualquier línea; sin valor. */
export function templateDraft(template) {
    return {
        ...emptyDraft(),
        recipients: {type: RECIPIENTS_ME, userIds: []},
        scope: {type: SCOPE_ANY, ids: []},
        metric: template.metric,
        operator: template.operator,
        period: template.period,
    };
}

/**
 * Las plantillas que el constructor de esta agencia puede armar: su métrica y su período están en
 * `options` (sin `options`, todas: es lo que ofrece el catálogo de siempre).
 */
export function availableTemplates(options) {
    const metrics = options && Array.isArray(options.metrics)
        ? new Set(options.metrics.map((metric) => metric.id)) : null;
    const periods = options && Array.isArray(options.periods) && options.periods.length
        ? new Set(options.periods.map((period) => period.id)) : null;
    return RULE_TEMPLATES.filter((template) => (!metrics || metrics.has(template.metric))
        && (!periods || periods.has(template.period)));
}

function idsOf(list) {
    return Array.isArray(list) ? list.map((id) => Number(id)).filter((id) => Number.isFinite(id)) : [];
}

// El separador decimal del idioma de la pantalla. En español «1.500» son mil quinientos y «1,5» uno y
// medio; en inglés, al revés (C-ac-fe-centro-03: «1.500» se guardaba 1,5).
function decimalSeparatorOf(locale) {
    return String(locale || 'es').toLowerCase().startsWith('en') ? '.' : ',';
}

/**
 * Un número escrito por una persona, leído con la notación del idioma de la pantalla:
 * - en español, el punto agrupa miles («1.500», «15.000», «1.500,5») y la coma es decimal («1,5»);
 * - en inglés, la coma agrupa («1,500», «1,500.5») y el punto es decimal («1.5»).
 * Un separador que no forma grupos de a 3 se toma como decimal aunque no sea el del idioma («1.5» en
 * español, «1,5» en inglés): es lo que quiso escribir quien lo tipeó. Acepta espacios y los signos de
 * unidad que se escapan («$500», «30%»). `null` si no es un número.
 */
export function parseValue(raw, locale = 'es') {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    let text = String(raw).replace(/[\s$%]/g, '');
    if (!text) return null;
    const decimal = decimalSeparatorOf(locale);
    const group = decimal === ',' ? '.' : ',';
    const esc = (char) => (char === '.' ? '\\.' : char);
    const grouped = new RegExp(`^-?\\d{1,3}(${esc(group)}\\d{3})+(${esc(decimal)}\\d+)?$`);
    if (grouped.test(text)) {
        text = text.split(group).join('').replace(decimal, '.');
    } else if (text.includes(decimal) && !text.includes(group)) {
        text = text.replace(decimal, '.');
    } else if (text.includes(group) && !text.includes(decimal)) {
        // «1.5» en español o «1,5» en inglés: un separador suelto que no agrupa de a 3 es decimal.
        text = text.replace(group, '.');
    }
    if (!/^-?\d*\.?\d+$/.test(text)) return null;
    const value = Number(text);
    return Number.isFinite(value) ? value : null;
}

/** Un número guardado escrito para editarlo en el idioma de la pantalla, sin agrupar miles. */
export function formatInputNumber(value, locale = 'es') {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    const text = String(number);
    return decimalSeparatorOf(locale) === ',' ? text.replace('.', ',') : text;
}

/** El borrador de una regla guardada (`RuleItem`), para editarla. */
export function draftFromRule(rule, {locale = 'es'} = {}) {
    const condition = (rule && rule.condition) || {};
    const scope = (rule && rule.scope) || {};
    const delivery = (rule && rule.delivery) || {};
    const recipients = delivery.recipients || {};
    let conditionType = condition.type === CONDITION_PERIOD_CHANGE ? CONDITION_PERIOD_CHANGE : CONDITION_ABSOLUTE;
    if (condition.type === CONDITION_CONTROL) conditionType = CONDITION_CONTROL;
    const operators = operatorsFor(conditionType === CONDITION_CONTROL ? CONDITION_ABSOLUTE : conditionType);
    return {
        // Una regla guardada sin canales (apagada desde su fila, v2) se edita así: completar «correo» la volvía a
        // prender al guardar otro cambio (review C-av2-fe-centro-09). Sin la clave, el correo de siempre.
        channels: Array.isArray(delivery.channels) ? [...delivery.channels] : [CHANNEL_EMAIL],
        recipients: {
            type: KNOWN_RECIPIENT_TYPES.includes(recipients.type) ? recipients.type : RECIPIENTS_ME,
            userIds: idsOf(recipients.user_ids),
            // Las bajas de una regla de equipo («salvo …», ronda 5): sólo le llegan al admin. `excludedKnown` dice si
            // vinieron; si no, el PUT no las manda (mandar `[]` las borraría).
            excludedUserIds: idsOf(recipients.excluded_user_ids),
            excludedKnown: Array.isArray(recipients.excluded_user_ids),
        },
        scope: {
            type: KNOWN_SCOPE_TYPES.includes(scope.type) ? scope.type : SCOPE_CLIENT,
            ids: idsOf(scope.ids),
        },
        metric: condition.metric || null,
        conditionType,
        operator: operators.includes(condition.operator) ? condition.operator : operators[0],
        value: formatInputNumber(condition.value, locale),
        period: condition.period || DEFAULT_PERIOD,
        vsPeriod: conditionType === CONDITION_PERIOD_CHANGE ? (condition.vs_period || null) : null,
        vsSource: condition.vs_source || null,
        // Una de Dashbo (ronda 5, D20) se edita con la métrica, el operador y el período fijos.
        systemLocked: isSystemRule(rule),
        name: (rule && rule.name) || '',
        nameTouched: true,
        isActive: !(rule && rule.is_active === false),
    };
}

/**
 * La copia de una regla para «Duplicar»: misma frase, nombre con el sufijo y prendida (la copia es
 * una regla nueva; nace activa aunque la original esté en pausa). Lleva sólo los ids que quien
 * duplica ve: la copia es suya.
 */
export function duplicateDraft(rule, copySuffix, {locale = 'es'} = {}) {
    const draft = draftFromRule(rule, {locale});
    return {
        ...draft,
        name: copyName(draft.name || '', copySuffix),
        // El nombre de la copia lo decide `copyDraftName` mientras nadie lo escriba (C-ac-fe-centro-27):
        // necesita saber cómo era la original.
        nameTouched: false,
        copyOf: {name: draft.name || '', draft},
        isActive: true,
        // La copia es una propia: compara los períodos que elija, no la columna fija, y no hereda las bajas.
        vsSource: null,
        systemLocked: false,
        recipients: {...draft.recipients, excludedUserIds: [], excludedKnown: false},
    };
}

/**
 * El nombre de una copia mientras nadie lo escribe (fede, 09/10, C-ac-fe-centro-27):
 * - Si la original tenía el nombre automático, la copia también: sigue a lo que se cambie (el
 *   período, el cliente, el umbral) y lleva el sufijo sólo mientras quedaría igual al de la original.
 *   Cuenta como automático también el de antes del 08/10, sin el período.
 * - Un nombre escrito a mano se conserva, con el sufijo (`copyName`).
 *
 * `autoName` es el nombre propuesto para el borrador de hoy; `originalCtx`, el contexto de la frase con
 * el alcance de la original.
 */
export function copyDraftName(copyOf, autoName, originalCtx, copySuffix) {
    const original = copyOf.name || '';
    if (!copyFollowsSentence(copyOf, originalCtx)) return copyName(original, copySuffix);
    return autoName === original ? copyName(autoName, copySuffix) : autoName;
}

/** ¿La original tenía el nombre automático? Entonces el de la copia sigue a la frase. */
export function copyFollowsSentence(copyOf, originalCtx) {
    const original = copyOf.name || '';
    return original !== '' && [copyOf.draft, {...copyOf.draft, period: ''}]
        .some((draft) => proposedName(draft, originalCtx) === original);
}

/**
 * El nombre de la copia, con el sufijo siempre y dentro del tope. Si el nombre termina en un paréntesis
 * (el período del nombre propuesto: «… · Dorfman (últimos 7 días)»), el sufijo va antes, para que el
 * período siga al final; lo que se recorta, con «…», es lo de adelante. Cortar el nombre entero en 80
 * se llevaba el «(copia)» (C-ac-fe-centro-27).
 */
function copyName(base, copySuffix) {
    if (!copySuffix) return base.slice(0, NAME_MAX_LENGTH);
    const match = base.match(/^(.*\S)( \([^()]+\))$/);
    const head = match ? match[1] : base;
    const tail = match ? match[2] : '';
    const suffix = ` ${copySuffix}`;
    const full = `${head}${suffix}${tail}`.trim();
    if (full.length <= NAME_MAX_LENGTH) return full;
    const room = NAME_MAX_LENGTH - suffix.length - tail.length - 1;
    if (room < 1) return full.slice(0, NAME_MAX_LENGTH);
    return `${head.slice(0, room).trimEnd()}…${suffix}${tail}`;
}

function isPeriodChangeDraft(draft) {
    return Boolean(draft) && draft.conditionType === CONDITION_PERIOD_CHANGE;
}

function conditionInput(draft, locale) {
    if (draft.conditionType === CONDITION_CONTROL) return {type: CONDITION_CONTROL};
    const input = {
        type: isPeriodChangeDraft(draft) ? CONDITION_PERIOD_CHANGE : CONDITION_ABSOLUTE,
        metric: draft.metric,
        operator: draft.operator,
        value: parseValue(draft.value, locale),
        period: draft.period,
    };
    if (isPeriodChangeDraft(draft)) input.vs_period = draft.vsPeriod;
    return input;
}

function scopeInput(draft) {
    return {type: draft.scope.type, ids: scopeHasIds(draft.scope.type) ? draft.scope.ids.slice() : []};
}

/**
 * El borrador al elegir un operador en la frase. Uno de «sube / cae / se aleja» pasa la condición a
 * `PERIOD_CHANGE` (y propone el primer período de comparación); uno de «por encima / por debajo» la vuelve a
 * umbral. Una métrica de la línea no admite comparación: su período es fijo.
 */
export function withOperator(draft, operator, vsPeriods = []) {
    const conditionType = conditionTypeOfOperator(operator);
    if (conditionType === CONDITION_ABSOLUTE) return {...draft, operator, conditionType, vsPeriod: null};
    return {...draft, operator, conditionType, vsPeriod: draft.vsPeriod || vsPeriods[0] || null};
}

/** La unidad del valor de la frase: la de la métrica, o «%» en una comparación contra otro período. */
export function valueUnitOf(draft, options) {
    if (isPeriodChangeDraft(draft) || isLineMetric(draft && draft.metric)) return UNIT_PERCENTAGE;
    return unitOfMetric(draft && draft.metric, options);
}

function deliveryInput(draft) {
    const userIds = draft.recipients.type === RECIPIENTS_USERS ? draft.recipients.userIds.slice() : [];
    const recipients = {type: draft.recipients.type, user_ids: userIds};
    // Las bajas viajan sólo en una de equipo y sólo si se conocen (las ve el admin) o se eligieron: sin la clave, el
    // backend deja las que hay; con `[]`, las borra (ronda 5).
    const excluded = draft.recipients.excludedUserIds || [];
    if (TEAM_RECIPIENT_TYPES.includes(draft.recipients.type) && (draft.recipients.excludedKnown || excluded.length)) {
        recipients.excluded_user_ids = excluded.slice();
    }
    return {channels: draft.channels.slice(), recipients};
}

/**
 * El `RuleInput` del contrato. `unit` NO viaja: la fija el backend por la métrica. `user_ids` sólo
 * con «a estas personas» y `ids` vacío sólo con «cualquier línea».
 */
export function ruleInputFromDraft(draft, {includeName = true, locale = 'es'} = {}) {
    const input = {
        is_active: draft.isActive !== false,
        condition: conditionInput(draft, locale),
        scope: scopeInput(draft),
        delivery: deliveryInput(draft),
    };
    if (includeName) input.name = (draft.name || '').trim();
    return input;
}

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const sortedIds = (ids) => [...ids].sort((a, b) => a - b);

/**
 * El PUT de una edición: SÓLO los bloques que cambiaron (C-ac-fe-centro-01). El backend reemplaza cada
 * bloque que llega, así que mandarlos todos tenía dos efectos: un usuario que ve parte del alcance lo
 * achicaba a lo suyo al tocar el umbral (`hidden_count`), y un admin no podía guardar un cambio de
 * umbral en una regla con una línea terminada o un destinatario que se fue, porque se revalidaba el
 * alcance entero. Un bloque que no se tocó no viaja. `{}` = nada que guardar.
 */
export function ruleUpdateFromDraft(rule, draft, {locale = 'es'} = {}) {
    const before = draftFromRule(rule, {locale});
    const update = {};
    const system = isSystemRule(rule);
    const control = isControlRule(rule);
    // Una de Dashbo no cambia de nombre (se titula por su clave) ni de métrica; de la condición viaja el número, y el
    // operador sólo en una comparación. Un aviso de control no tiene condición ni cambia su alcance (ronda 5, R).
    const name = (draft.name || '').trim();
    if (!system && name !== (rule.name || '')) update.name = name;
    if ((draft.isActive !== false) !== (before.isActive !== false)) update.is_active = draft.isActive !== false;
    if (!control && !sameJson(conditionInput(draft, locale), conditionInput(before, locale))) {
        if (system) {
            const condition = {value: parseValue(draft.value, locale)};
            if (isPeriodChangeDraft(draft) && draft.operator !== before.operator) condition.operator = draft.operator;
            update.condition = condition;
        } else {
            update.condition = conditionInput(draft, locale);
        }
    }
    const scopeNow = scopeInput(draft);
    const scopeBefore = scopeInput(before);
    if (!control && (scopeNow.type !== scopeBefore.type || !sameJson(sortedIds(scopeNow.ids), sortedIds(scopeBefore.ids)))) {
        update.scope = scopeNow;
    }
    const deliveryNow = deliveryInput(draft);
    const deliveryBefore = deliveryInput(before);
    if (!sameJson([...deliveryNow.channels].sort(), [...deliveryBefore.channels].sort())
        || deliveryNow.recipients.type !== deliveryBefore.recipients.type
        || !sameJson(sortedIds(deliveryNow.recipients.user_ids), sortedIds(deliveryBefore.recipients.user_ids))
        || !sameJson(sortedIds(deliveryNow.recipients.excluded_user_ids || []),
            sortedIds(deliveryBefore.recipients.excluded_user_ids || []))) {
        update.delivery = deliveryNow;
    }
    return update;
}

/** Los topes del backend (`limits`), con los de siempre si no vienen. */
export function limitsOf(limits) {
    return {
        maxRecipients: limits && typeof limits.max_recipients === 'number' ? limits.max_recipients : MAX_RECIPIENTS,
        maxScopeIds: limits && typeof limits.max_scope_ids === 'number' ? limits.max_scope_ids : MAX_SCOPE_IDS,
    };
}

/**
 * Los errores del borrador, por campo: `{campo: clave i18n}`. Vacío = se puede simular y guardar.
 * `requireName`: el nombre es obligatorio al guardar, no al simular (el contrato lo saca del simulate).
 * `limits`: los topes que manda el backend (`max_recipients`, `max_scope_ids`).
 */
export function validateDraft(draft, {requireName = false, locale = 'es', limits = null, allowNoChannels = false} = {}) {
    const {maxRecipients, maxScopeIds} = limitsOf(limits);
    const errors = {};
    // En una agencia convertida una regla sin canales es válida: está «Apagada», no le avisa a nadie (v2).
    if (!allowNoChannels && (!draft.channels || draft.channels.length === 0)) {
        errors.channels = 'alerts_center.builder.error_channels';
    }
    if (draft.recipients.type === RECIPIENTS_USERS) {
        if (draft.recipients.userIds.length === 0) errors.recipients = 'alerts_center.builder.error_recipients';
        else if (draft.recipients.userIds.length > maxRecipients) {
            errors.recipients = 'alerts_center.builder.error_recipients_max';
        }
    }
    if (scopeHasIds(draft.scope.type)) {
        if (draft.scope.ids.length === 0) errors.scope = 'alerts_center.builder.error_scope';
        else if (draft.scope.ids.length > maxScopeIds) errors.scope = 'alerts_center.builder.error_scope_max';
    }
    // Un aviso de control (ronda 5) no tiene métrica, número ni período: se edita el «Para» y los canales.
    if (draft.conditionType !== CONDITION_CONTROL) {
        if (!draft.metric) errors.metric = 'alerts_center.builder.error_metric';
        const periodChange = isPeriodChangeDraft(draft);
        if (!operatorsFor(periodChange ? CONDITION_PERIOD_CHANGE : CONDITION_ABSOLUTE).includes(draft.operator)) {
            errors.operator = 'alerts_center.builder.error_operator';
        } else if (periodChange && isLineMetric(draft.metric)) {
            // Las de la línea tienen un período fijo: no hay «contra el período anterior» que medir.
            errors.operator = 'alerts_center.builder.error_line_period_change';
        }
        const value = parseValue(draft.value, locale);
        if (value === null) errors.value = 'alerts_center.builder.error_value';
        else if (periodChange && !(value > 0)) errors.value = 'alerts_center.builder.error_value_positive';
        if (!draft.period) errors.period = 'alerts_center.builder.error_period';
        if (periodChange && !draft.vsPeriod) errors.vsPeriod = 'alerts_center.builder.error_vs_period';
    }
    const name = (draft.name || '').trim();
    if (name.length > NAME_MAX_LENGTH) errors.name = 'alerts_center.builder.error_name_long';
    else if (requireName && !name) errors.name = 'alerts_center.builder.error_name';
    return errors;
}

// ------------------------------------------------------------------ errores del backend ----------

// El texto de cada `error_code` del contrato (§2), y el chip al que apunta. Nunca `error_message`
// crudo: puede ser el `detail` en inglés de DRF o un genérico que inventa el `APIClient`.
const ERROR_CODES = {
    INVALID_METRIC: {field: 'metric', key: 'alerts_center.builder.error_metric'},
    INVALID_OPERATOR: {field: 'operator', key: 'alerts_center.builder.error_operator'},
    INVALID_VALUE: {field: 'value', key: 'alerts_center.builder.error_value'},
    INVALID_PERIOD: {field: 'period', key: 'alerts_center.builder.error_period'},
    INVALID_SCOPE: {field: 'scope', key: 'alerts_center.builder.error_scope'},
    INVALID_RECIPIENTS: {field: 'recipients', key: 'alerts_center.builder.error_recipients'},
    LIMIT_REACHED: {field: null, key: 'alerts_center.builder.limit_reached'},
    NAME_TOO_LONG: {field: 'name', key: 'alerts_center.builder.error_name_long'},
    // 403 tipado de los endpoints del Centro y del POST de sensibilidades (C-ac-fe-centro-05):
    // reintentar no sirve nunca, así que no se dice «vuelve a intentarlo».
    ERROR_USER_DOES_NOT_HAVE_ACCESS: {field: null, key: 'alerts_center.no_edit_permission'},
    // 404 uniforme: la regla se borró (otra pestaña, otra persona) o ya no se ve (C-ac-fe-centro-23).
    NOT_FOUND: {field: null, key: 'alerts_center.rule_not_found'},
    // v2 (contrato v2 §2 y §4). Una de Dashbo no cambia de métrica ni de alcance.
    SYSTEM_FIELD_LOCKED: {field: null, key: 'alerts_center.editor.error_locked'},
    CANNOT_DELETE_SYSTEM: {field: null, key: 'alerts_center.row.cannot_delete_system'},
    // Prender el webhook de una fila sin URL: la pantalla abre el panel del webhook.
    WEBHOOK_NOT_CONFIGURED: {field: null, key: 'alerts_center.row.webhook_not_configured'},
    // Un canal que ese aviso no tiene (`delivery.available_channels`): el interruptor no se dibuja, pero otra
    // pestaña con un payload viejo puede pedirlo.
    UNSUPPORTED_CHANNEL: {field: null, key: 'alerts_center.row.unsupported_channel'},
    // Ronda 4 (D13): las de la agencia y avisar a otros los toca sólo un admin.
    ADMIN_REQUIRED: {field: null, key: 'alerts_center.admin_required'},
    // Ronda 5 (D19): la baja propia es sólo de las reglas de equipo; en una con personas puntuales edita el admin.
    NOT_A_TEAM_RULE: {field: null, key: 'alerts_center.row.not_a_team_rule'},
    // «+ Agregar» una sugerida que otra pestaña ya agregó: la lista se recarga.
    ALREADY_EXISTS: {field: null, key: 'alerts_center.suggestions.already_exists'},
};

export const ERROR_LIMIT_REACHED = 'LIMIT_REACHED';
export const ERROR_NO_EDIT_PERMISSION = 'ERROR_USER_DOES_NOT_HAVE_ACCESS';
export const ERROR_NOT_FOUND = 'NOT_FOUND';
export const ERROR_WEBHOOK_NOT_CONFIGURED = 'WEBHOOK_NOT_CONFIGURED';
export const ERROR_ALREADY_EXISTS = 'ALREADY_EXISTS';
export const ERROR_INVALID_VALUE = 'INVALID_VALUE';

// El `field` del backend viene con la ruta del bloque (`condition.metric`, `scope.ids`,
// `delivery.recipients.user_ids`…): los chips se llaman por el campo del borrador (C-ac-fe-centro-04).
const FIELD_BY_PATH = [
    ['condition.metric', 'metric'],
    ['condition.type', 'metric'],
    ['condition.operator', 'operator'],
    ['condition.value', 'value'],
    ['condition.period', 'period'],
    ['condition.vs_period', 'vsPeriod'],
    ['condition', 'metric'],
    ['scope', 'scope'],
    ['delivery.channels', 'channels'],
    ['delivery.recipients', 'recipients'],
    ['delivery', 'recipients'],
    ['name', 'name'],
];

export function fieldFromPath(path) {
    if (!path) return null;
    const found = FIELD_BY_PATH.find(([prefix]) => path === prefix || path.startsWith(`${prefix}.`));
    return found ? found[1] : null;
}

/**
 * `{code, field, key}` de una respuesta que no fue OK. El código puede venir en `error_code` (cuerpo
 * de un 4xx, lo copia el `APIClient`) o en `status` (un 200 con status tipado). Un código que el
 * contrato no nombra —incluido el `NETWORK_ERROR`— cae al genérico. El `field` del backend (en
 * `raw_body`: el `APIClient` no lo copia arriba) manda sobre el del código: un `INVALID_RECIPIENTS`
 * en `delivery.channels` es el webhook que no está en el plan, no un problema de destinatarios.
 */
export function errorFromResponse(response, {fallbackKey = 'alerts_center.builder.save_error'} = {}) {
    const body = (response && response.raw_body) || {};
    const code = (response && (response.error_code || response.status)) || body.error_code || null;
    const known = code && ERROR_CODES[code];
    if (!known) return {code, field: null, key: fallbackKey};
    if (known.field === null) return {code, field: null, key: known.key};
    const field = fieldFromPath((response && response.field) || body.field) || known.field;
    const key = field === 'channels' ? 'alerts_center.builder.error_webhook_plan' : known.key;
    return {code, field, key};
}

// ---------------------------------------------------------------------------- frase ---------------

/** La unidad de una métrica: la que manda el backend en `options`; sin ella, la del catálogo del FE. */
export function unitOfMetric(metric, options) {
    if (!metric) return null;
    const known = options && Array.isArray(options.metrics)
        ? options.metrics.find((item) => item.id === metric) : null;
    if (known && known.unit) return known.unit;
    if (String(metric).startsWith('CUSTOM:')) return UNIT_NUMBER;
    const badge = getValueTypeBadge(metric);
    if (badge === '$') return UNIT_CURRENCY;
    if (badge === '%') return UNIT_PERCENTAGE;
    return UNIT_NUMBER;
}

export function unitBadge(unit) {
    if (unit === UNIT_CURRENCY) return '$';
    if (unit === UNIT_PERCENTAGE) return '%';
    return '#';
}

/**
 * El valor del umbral tal como se lee en la frase: «2», «30 %», «$500». Sin moneda: una regla
 * multi-cliente se compara en la moneda de cada línea (README §2.1).
 */
export function formatThreshold(value, unit, locale = 'es') {
    const number = parseValue(value, locale);
    if (number === null) return '';
    let text;
    try {
        text = new Intl.NumberFormat(locale, {maximumFractionDigits: 2}).format(number);
    } catch (e) {
        text = String(number);
    }
    if (unit === UNIT_PERCENTAGE) return `${text} %`;
    if (unit === UNIT_CURRENCY) return `$${text}`;
    return text;
}

function lowerFirst(text) {
    if (!text) return '';
    return text.charAt(0).toLowerCase() + text.slice(1);
}

/** Los nombres que el alcance elige, desde el roster de `options` (constructor). */
export function scopeNamesFromOptions(scope, options) {
    if (!scope || !scopeHasIds(scope.type)) return [];
    const ids = new Set(scope.ids || []);
    if (!options) return [];
    if (scope.type === SCOPE_CLIENT) {
        return (options.clients || []).filter((client) => ids.has(client.id)).map((client) => client.name);
    }
    if (scope.type === SCOPE_LINE) {
        // «Cliente · Línea»: dos clientes pueden tener una línea «Search» (C-ac-fe-centro-09).
        const names = [];
        (options.clients || []).forEach((client) => (client.lines || []).forEach((line) => {
            if (ids.has(line.id)) names.push(lineName(client.name, line.name));
        }));
        return names;
    }
    if (scope.type === SCOPE_LABEL) {
        return (options.labels || []).filter((label) => ids.has(label.id)).map((label) => label.name);
    }
    return [];
}

function lineName(clientName, name) {
    return clientName ? `${clientName} · ${name}` : name;
}

/**
 * Los nombres de una regla guardada: el backend los manda resueltos (`scope.resolved`), las líneas con
 * su cliente (`client_name`).
 */
export function scopeNamesFromRule(rule) {
    const scope = (rule && rule.scope) || {};
    const resolved = Array.isArray(scope.resolved) ? scope.resolved : [];
    return resolved
        .map((item) => (scope.type === SCOPE_LINE ? lineName(item.client_name, item.name) : item.name))
        .filter(Boolean);
}

/**
 * Cuántos ids del alcance guardado NO ve quien mira (`scope.hidden_count`): un usuario con clientes
 * asignados ve sólo los suyos. La regla los conserva; la frase lo dice y el alcance no se edita.
 */
export function hiddenScopeCount(rule) {
    const count = rule && rule.scope ? Number(rule.scope.hidden_count) : 0;
    return Number.isFinite(count) && count > 0 ? count : 0;
}

/**
 * Los ids guardados que ya no están en el roster de `options`: una línea terminada, un cliente
 * desactivado, una etiqueta borrada. Guardarlos de nuevo da `INVALID_SCOPE`.
 */
export function staleScopeIds(scope, options) {
    if (!scope || !scopeHasIds(scope.type) || !options) return [];
    const available = new Set();
    if (scope.type === SCOPE_CLIENT) (options.clients || []).forEach((client) => available.add(client.id));
    if (scope.type === SCOPE_LINE) {
        (options.clients || []).forEach((client) => (client.lines || []).forEach((line) => available.add(line.id)));
    }
    if (scope.type === SCOPE_LABEL) (options.labels || []).forEach((label) => available.add(label.id));
    return (scope.ids || []).filter((id) => !available.has(id));
}

/** Los destinatarios elegidos que ya no están en el roster (`options.users`): alguien que se fue. */
export function staleRecipientIds(recipients, options) {
    if (!recipients || recipients.type !== RECIPIENTS_USERS || !options) return [];
    const available = new Set((options.users || []).map((user) => user.id));
    return (recipients.userIds || []).filter((id) => !available.has(id));
}

export function joinNames(names, t) {
    if (!names || names.length === 0) return '';
    if (names.length === 1) return names[0];
    return t('alerts_center.builder.names_and_more', {
        first: names[0], count: names.length - 1, interpolation: {escapeValue: false},
    });
}

export function recipientUsersFromOptions(recipients, options) {
    if (!recipients || recipients.type !== RECIPIENTS_USERS || !options) return [];
    const ids = new Set(recipients.userIds || []);
    return (options.users || []).filter((user) => ids.has(user.id)).map((user) => user.name);
}

/**
 * La frase de la regla como tokens: `{type: 'word'|'chip', slot?, text, empty?}`. El constructor
 * convierte cada chip en un botón; la fila de la lista la dibuja de corrido.
 *
 * `ctx`:
 *  - `t`, `locale`.
 *  - `metricLabel(metric)` y `periodLabel(period)`: los nombres legibles (los resuelve quien llama,
 *    con el catálogo de métricas y las personalizadas de la agencia).
 *  - `scopeNames`: los nombres que elige el alcance.
 *  - `recipientNames`: con «a estas personas», a quiénes.
 *  - `unit`: la unidad de la métrica.
 *  - `hiddenCount`: cuántos ids del alcance no ve quien mira (`scope.hidden_count`).
 */
export function sentenceTokens(draft, ctx) {
    const {t} = ctx;
    const tokens = [];
    const word = (key) => tokens.push({type: 'word', text: t(key)});
    const chip = (slot, text, empty = false) => tokens.push({type: 'chip', slot, text, empty});

    word('alerts_center.builder.sentence_notify');
    // Un borrador de «En tus palabras» (F4) puede dejar canal, condición o período VACÍOS (la IA no
    // los entendió): el chip lo dice en vez de mostrar un default que nadie eligió.
    const noChannels = !draft.channels || draft.channels.length === 0;
    // En una agencia convertida (ronda 5, D18) el correo va siempre: un chip fijo, con candado. El webhook es otro chip
    // de la frase, que se prende y se apaga (UD-18: «Avisar por [Correo] y [Webhook]», nunca un switch aparte). Se
    // ofrece si la agencia lo tiene y el aviso lo admite (`ctx.webhookOffered`); si la regla ya lo usa, se ve igual.
    if (ctx.emailAlways) {
        tokens.push({type: 'chip', slot: 'channelEmail', text: t('alerts_center.row.channel_email'), empty: false, fixed: true});
        const webhookOn = (draft.channels || []).includes(CHANNEL_WEBHOOK);
        if (ctx.webhookOffered || webhookOn) {
            if (webhookOn) word('alerts_center.builder.sentence_and');
            tokens.push({type: 'chip', slot: 'channelWebhook', text: t('alerts_center.row.channel_webhook'), empty: false,
                // En una regla que se lee y no se cambia («Ver»), tampoco se toca (review C-ud18-fe-05).
                on: webhookOn, disabled: !ctx.webhookOffered || ctx.webhookReady === false || ctx.editable === false});
        }
    } else if (noChannels && ctx.allowNoChannels) chip('channels', t('alerts_center.builder.channel_none'));
    else chip('channels', noChannels ? t('alerts_center.builder.pick_channel') : t(channelsKey(draft.channels)), noChannels);
    chip('recipients', t(recipientKey(draft.recipients.type)));
    if (draft.recipients.type === RECIPIENTS_USERS) {
        const names = ctx.recipientNames || [];
        chip('recipientUsers', names.length ? joinNames(names, t) : t('alerts_center.builder.pick_users'),
            names.length === 0);
    }
    // «… a todo el equipo de cada cliente [salvo Ana]» (ronda 5, D17): las bajas de una regla de equipo las edita el admin.
    if (ctx.canExclude && TEAM_RECIPIENT_TYPES.includes(draft.recipients.type)) {
        const names = ctx.excludedNames || [];
        chip('recipientExcept', names.length
            ? t('alerts_center.builder.except_names', {names: joinNames(names, t), interpolation: {escapeValue: false}})
            : t('alerts_center.builder.except_none'), names.length === 0);
    }
    // Un aviso de control (ronda 5): sin número ni métrica; su alcance lo pone el aviso y no se elige.
    if (draft.conditionType === CONDITION_CONTROL) {
        word('alerts_center.builder.sentence_control');
        tokens.push({type: 'chip', slot: 'control', text: ctx.controlLabel || '', empty: false, fixed: true});
        if (ctx.controlScope) tokens.push({type: 'chip', slot: 'scopeType', text: ctx.controlScope, empty: false, fixed: true});
        return tokens;
    }
    word('alerts_center.builder.sentence_when');
    chip('scopeType', t(scopeKey(draft.scope.type)));
    if (scopeHasIds(draft.scope.type)) {
        const names = ctx.scopeNames || [];
        const hidden = ctx.hiddenCount || 0;
        let text = names.length ? joinNames(names, t) : t(SCOPE_PICK_KEYS[draft.scope.type]);
        // Lo que quien mira no ve también es parte de la regla: se nombra, no se esconde.
        if (hidden) {
            text = names.length
                ? t('alerts_center.builder.scope_plus_hidden', {names: text, count: hidden, interpolation: {escapeValue: false}})
                : t('alerts_center.builder.scope_only_hidden', {count: hidden});
        }
        chip('scopeValue', text, names.length === 0 && !hidden);
    }
    word('alerts_center.builder.sentence_has');
    // Una de Dashbo (ronda 5, D20): la métrica se lee y no se cambia («% del presupuesto invertido · de Dashbo»).
    const lockedHint = 'alerts_center.builder.metric_fixed_hint';
    if (draft.systemLocked) {
        tokens.push({type: 'chip', slot: 'metric', empty: false, fixed: true, hintKey: lockedHint,
            text: t('alerts_center.builder.metric_of_dashbo', {metric: ctx.metricLabel(draft.metric), interpolation: {escapeValue: false}})});
    } else {
        chip('metric', draft.metric ? ctx.metricLabel(draft.metric) : t('alerts_center.builder.pick_metric'),
            !draft.metric);
    }
    // «… tenga [CPA] que [sube más de] [25 %] en [este mes] contra [el mes anterior]» (v2).
    const periodChange = isPeriodChangeDraft(draft);
    if (periodChange) word('alerts_center.builder.sentence_that');
    // En una de Dashbo el operador se cambia sólo en una comparación (sube / cae / se aleja); el de un umbral, no.
    if (draft.systemLocked && !periodChange) {
        tokens.push({type: 'chip', slot: 'operator', empty: false, fixed: true, hintKey: lockedHint,
            text: t(operatorKey(draft.operator))});
    } else {
        chip('operator', draft.operator ? t(operatorKey(draft.operator)) : t('alerts_center.builder.pick_operator'),
            !draft.operator);
    }
    const valueText = formatThreshold(draft.value, periodChange ? UNIT_PERCENTAGE : ctx.unit, ctx.locale);
    chip('value', valueText || t('alerts_center.builder.value_placeholder'), !valueText);
    // ROAS / ACOS contra el mes pasado de Dashbo: los dos períodos son los de la columna fija, se leen y no se eligen.
    if (periodChange && draft.vsSource === VS_SOURCE_FIXED_COLUMN) {
        const hintKey = 'alerts_center.builder.vs_fixed_column_hint';
        word('alerts_center.builder.sentence_in');
        tokens.push({type: 'chip', slot: 'period', text: lowerFirst(ctx.periodLabel(draft.period)) || draft.period,
            empty: false, fixed: true, hintKey});
        word('alerts_center.builder.sentence_against');
        tokens.push({type: 'chip', slot: 'vsPeriod', text: lowerFirst(ctx.periodLabel(draft.vsPeriod)) || draft.vsPeriod,
            empty: false, fixed: true, hintKey});
        return tokens;
    }
    // Una métrica de la línea se mide siempre en su período: el chip lo dice y no se elige.
    if (isLineMetric(draft.metric)) {
        word('alerts_center.builder.sentence_in');
        tokens.push({type: 'chip', slot: 'period', text: t('alerts_center.builder.period_fixed'), empty: false, fixed: true});
        return tokens;
    }
    // Una de Dashbo mide en los períodos con los que nació (una comparativa de la agencia): se leen y no se eligen.
    if (draft.systemLocked) {
        if (!DAY_PERIODS.includes(draft.period)) word('alerts_center.builder.sentence_in');
        tokens.push({type: 'chip', slot: 'period', empty: false, fixed: true, hintKey: lockedHint,
            text: lowerFirst(ctx.periodLabel(draft.period)) || draft.period});
        if (periodChange && draft.vsPeriod) {
            word('alerts_center.builder.sentence_against');
            tokens.push({type: 'chip', slot: 'vsPeriod', empty: false, fixed: true, hintKey: lockedHint,
                text: lowerFirst(ctx.periodLabel(draft.vsPeriod)) || draft.vsPeriod});
        }
        return tokens;
    }
    // «… en este período», pero «… $500 ayer»: con un día, el «en» sobra (la regla de Pablo y la
    // plantilla de Costo se leían «en ayer», 14/10).
    if (!DAY_PERIODS.includes(draft.period)) word('alerts_center.builder.sentence_in');
    chip('period', draft.period ? (lowerFirst(ctx.periodLabel(draft.period)) || draft.period)
        : t('alerts_center.builder.pick_period'), !draft.period);
    if (periodChange) {
        word('alerts_center.builder.sentence_against');
        chip('vsPeriod', draft.vsPeriod ? (lowerFirst(ctx.periodLabel(draft.vsPeriod)) || draft.vsPeriod)
            : t('alerts_center.builder.pick_vs_period'), !draft.vsPeriod);
    }
    return tokens;
}

export function sentenceText(tokens) {
    return tokens.map((token) => token.text).join(' ');
}

/**
 * El nombre que se propone mientras el usuario no escribe uno: «CPA por encima de $3000 · Dorfman
 * (últimos 7 días)». El período va al final, entre paréntesis y con el texto del chip: dos reglas
 * iguales sobre períodos distintos se distinguen por el nombre (fede, 08/10). Sin métrica todavía,
 * vacío (un nombre a medias se lee como un error).
 */
export function proposedName(draft, ctx) {
    if (!draft.metric) return '';
    const {t} = ctx;
    const valueText = formatThreshold(draft.value, ctx.unit, ctx.locale);
    // La condición entra entera o no entra: «ROAS por debajo de» se quedaba como nombre si el usuario lo
    // tocaba antes de completar el valor (review C-ac-nl-05 de la ronda 2, con un borrador de «En tus
    // palabras», que deja vacío lo dudoso).
    const complete = Boolean(draft.operator && valueText);
    const condition = [ctx.metricLabel(draft.metric), complete ? t(operatorKey(draft.operator)) : null,
        complete ? valueText : null].filter(Boolean).join(' ');
    let scopeName;
    if (draft.scope.type === SCOPE_ANY) scopeName = {name: t('alerts_center.builder.scope_any_short'), more: ''};
    else if (draft.scope.type === SCOPE_AGENCY) scopeName = {name: t('alerts_center.builder.scope_agency_short'), more: ''};
    else scopeName = scopeNameParts(ctx.scopeNames || [], t);
    const {name: where, more} = scopeName;
    // Una de la línea tiene período fijo: el nombre no lo repite.
    let period = draft.period && !isLineMetric(draft.metric)
        ? (lowerFirst(ctx.periodLabel(draft.period)) || draft.period) : '';
    // Una comparación dice contra qué: «(este mes contra el mes anterior)» (review C-av2-fe-centro-22).
    if (period && isPeriodChangeDraft(draft) && draft.vsPeriod) {
        period = t('alerts_center.builder.name_period_vs', {
            period, vs: lowerFirst(ctx.periodLabel(draft.vsPeriod)) || draft.vsPeriod, interpolation: {escapeValue: false},
        });
    }
    return fitName(condition, where, more, period ? ` (${inlineParens(period)})` : '');
}

// Con menos lugar que esto, el alcance recortado no dice nada («D…»): se saca entero.
const MIN_SCOPE_CHARS = 6;

/**
 * El alcance partido en el nombre que se puede recortar y lo que no: en «Distribuidora del Litoral y 1
 * más», el recorte va sobre el cliente y «y 1 más» queda entero, o el nombre deja de decir que eran
 * varios (QA del 09/10). Si la plantilla del idioma no empieza con el nombre, va entera como antes.
 */
function scopeNameParts(names, t) {
    if (names.length < 2) return {name: joinNames(names, t), more: ''};
    const marker = '\u0000';
    const joined = joinNames([marker, ...names.slice(1)], t);
    if (!joined.startsWith(marker)) return {name: joinNames(names, t), more: ''};
    return {name: names[0], more: joined.slice(marker.length)};
}

// Un período que ya trae paréntesis («este mes (hasta ayer)») dentro del paréntesis del nombre queda
// anidado: adentro va con coma, «(este mes, hasta ayer)». El chip lo sigue mostrando como siempre.
function inlineParens(text) {
    return text.replace(/\s*\(([^()]*)\)/g, ', $1');
}

/**
 * Arma «condición · alcance (período)» dentro de `NAME_MAX_LENGTH`. Lo que se recorta es el nombre del
 * alcance, con «…» (`more`, el «y N más», queda entero); el período nunca. Si ni la condición con el
 * período entran, se recorta la condición.
 */
function fitName(condition, where, more, tail) {
    const full = where ? `${condition} · ${where}${more}${tail}` : `${condition}${tail}`;
    if (full.length <= NAME_MAX_LENGTH) return full;
    const room = NAME_MAX_LENGTH - condition.length - ' · '.length - more.length - tail.length;
    if (where && room >= MIN_SCOPE_CHARS) return `${condition} · ${where.slice(0, room - 1).trimEnd()}…${more}${tail}`;
    return `${condition.slice(0, NAME_MAX_LENGTH - tail.length).trimEnd()}${tail}`;
}

/**
 * Las monedas de las líneas que toca el alcance (constructor). Con más de una, la UI avisa que cada
 * línea se compara en la suya. `ANY_LINE` mira todas las líneas que el usuario ve. Las etiquetas no
 * traen sus líneas en `options`: sin datos, no se afirma nada (Set vacío).
 */
export function scopeCurrencies(scope, options) {
    const currencies = new Set();
    if (!scope || !options) return currencies;
    const ids = new Set(scope.ids || []);
    (options.clients || []).forEach((client) => {
        const clientInScope = !scopeHasIds(scope.type) || (scope.type === SCOPE_CLIENT && ids.has(client.id));
        (client.lines || []).forEach((line) => {
            const inScope = clientInScope || (scope.type === SCOPE_LINE && ids.has(line.id));
            if (inScope && line.currency) currencies.add(line.currency);
        });
    });
    return currencies;
}

/**
 * ¿Hay que avisar «se compara en la moneda de cada línea»? Con una métrica de moneda, cuando el
 * alcance cruza monedas, y SIEMPRE con «las líneas con la etiqueta»: `options.labels` no trae las
 * monedas de sus líneas, y es justo el alcance que cruza clientes (C-ac-fe-centro-14).
 */
export function needsCurrencyNote(scope, unit, options) {
    if (unit !== UNIT_CURRENCY || !scope) return false;
    if (scope.type === SCOPE_LABEL) return true;
    return scopeCurrencies(scope, options).size > 1;
}

/**
 * Los canales de las líneas del alcance, para filtrar el selector de métricas. `null` = no se sabe
 * (etiquetas, o nada elegido todavía): el selector ofrece el catálogo entero.
 */
export function scopeChannels(scope, options) {
    if (!scope || !options || scope.type === SCOPE_LABEL) return null;
    const ids = new Set(scope.ids || []);
    if (scopeHasIds(scope.type) && ids.size === 0) return null;
    const channels = new Set();
    (options.clients || []).forEach((client) => {
        const clientInScope = !scopeHasIds(scope.type) || (scope.type === SCOPE_CLIENT && ids.has(client.id));
        (client.lines || []).forEach((line) => {
            const inScope = clientInScope || (scope.type === SCOPE_LINE && ids.has(line.id));
            if (inScope) (line.channels || []).forEach((channel) => channels.add(channel));
        });
    });
    return channels.size ? [...channels] : null;
}

/** Las personalizadas de `options.metrics`, con la forma que espera `MetricCombobox`. */
export function customMetricsForCombobox(options) {
    if (!options || !Array.isArray(options.metrics)) return [];
    return options.metrics
        .filter((metric) => String(metric.id).startsWith('CUSTOM:'))
        .map((metric) => ({metric_key: metric.id, display_name: metric.label}));
}

/** Las de la línea (`LINE:*`, grupo «De la línea») de `options.metrics`, para el selector del constructor. */
export function lineMetricsForCombobox(options) {
    if (!options || !Array.isArray(options.metrics)) return [];
    return options.metrics
        .filter((metric) => isLineMetric(metric.id) || metric.group === 'LINE')
        .map((metric) => ({metric_key: metric.id, display_name: metric.label}));
}

// Los períodos de comparación si `options` no los trae: los que dependen de otro (README v2 §2.3).
const FALLBACK_VS_PERIODS = ['PREVIOUS_PERIOD', '7_DAYS_BEFORE', '14_DAYS_BEFORE', 'SAME_PERIOD_LAST_YEAR'];

/** Contra qué períodos se puede comparar (`options.vs_periods`, contrato v2 §6). */
export function vsPeriodsOf(options) {
    const list = options && Array.isArray(options.vs_periods) ? options.vs_periods : null;
    if (!list || !list.length) return FALLBACK_VS_PERIODS;
    return list.map((period) => (period && typeof period === 'object' ? period.id : period)).filter(Boolean);
}

/**
 * El paso del [−] [+] del editor del número: 5 en porcentaje; en plata y en números, uno a la escala del
 * valor (3.000 → 100, 500 → 10, 2 → 0,1), para que dos clics muevan algo que se note y no lo desarmen.
 */
export function valueStep(unit, value) {
    if (unit === UNIT_PERCENTAGE) return 5;
    const number = Math.abs(Number(value));
    if (!Number.isFinite(number) || number === 0) return 1;
    const step = 10 ** (Math.floor(Math.log10(number)) - 1);
    return unit === UNIT_CURRENCY ? Math.max(1, step) : step;
}

/** El valor después de un [−] o un [+], sin el ruido de los decimales flotantes y nunca negativo. */
export function stepValue(value, step, direction) {
    const base = Number.isFinite(Number(value)) ? Number(value) : 0;
    const decimals = step < 1 ? Math.ceil(-Math.log10(step)) : 0;
    return Math.max(0, Number((base + direction * step).toFixed(decimals)));
}

// ------------------------------------------------------------------------- la lista --------------

// «Avisan» salió (recorrido con Pablo, 13/10, C-07): contaba lo que deja aviso en la Bitácora o el
// webhook, y nadie lo leía así. Un `?filtro=avisan` viejo abre «Todas», como cualquier valor que no existe.
export const FILTER_ALL = 'todas';
export const FILTER_EMAIL = 'mandan-correo';
export const FILTER_NOTICE = 'dejan-aviso';
export const FILTER_PAINT = 'solo-pintan';
export const FILTER_PAUSED = 'en-pausa';
export const FILTER_MINE = 'mias';
export const RULE_FILTERS = [FILTER_ALL, FILTER_EMAIL, FILTER_NOTICE, FILTER_PAINT, FILTER_PAUSED, FILTER_MINE];

export const FILTER_LABEL_KEYS = {
    [FILTER_ALL]: 'alerts_center.rules.filter_all',
    [FILTER_EMAIL]: 'alerts_center.rules.filter_email',
    [FILTER_NOTICE]: 'alerts_center.rules.filter_notice',
    [FILTER_PAINT]: 'alerts_center.rules.filter_paint',
    [FILTER_PAUSED]: 'alerts_center.rules.filter_paused',
    [FILTER_MINE]: 'alerts_center.rules.filter_mine',
};

// Qué hace HOY una regla, en una palabra. Es una partición: cada regla tiene una sola, y «Todas» es la
// suma de las cuatro.
export const USE_EMAIL = 'EMAIL';
export const USE_NOTICE = 'NOTICE';
export const USE_PAINT = 'PAINT';
export const USE_PAUSED = 'PAUSED';

export function filterFromParam(value) {
    return RULE_FILTERS.includes(value) ? value : FILTER_ALL;
}

export function isCustom(item) {
    return item && item.kind === KIND_CUSTOM;
}

export const PAUSED_CREATOR_LEFT = 'CREATOR_LEFT';
export const PAUSED_LIMIT = 'LIMIT';

/**
 * ¿La regla está activa? Una con `paused_reason` está pausada aunque el backend la mande
 * `is_active: true`: «se fue quien la creó» se calcula en vivo y no apaga el flag (F0 C-06), y el
 * interruptor no puede quedar prendido al lado de «En pausa» (C-ac-fe-centro-08).
 */
export function isActiveRule(item) {
    return Boolean(item) && item.is_active !== false && !item.deleted_on && !item.paused_reason;
}

// Sin quien mire, el rótulo «Correo» de la regla: el que eligió la propia, o la de Dashbo que tiene correo.
function defaultEmailOn(item) {
    if (isCustom(item)) return ((item.delivery && item.delivery.channels) || []).includes(CHANNEL_EMAIL);
    return Boolean(item.delivery && item.delivery.email && item.delivery.email.has_email === true);
}

/**
 * Qué hace HOY la regla para quien mira (C-06/C-07):
 * - `EMAIL`: activa y con el rótulo «Correo» prendido (una propia que le llega; una de Dashbo que le llega
 *   según «Mis alertas»).
 * - `NOTICE`: activa y avisa sin correo: deja el aviso en la Bitácora y, si está, en el webhook.
 * - `PAINT`: activa y de las que no avisan: pinta su columna del Tablero (o ya ni eso, si le sacaron el
 *   color desde la tuerca: sigue siendo de esa familia).
 * - `PAUSED`: en pausa; no pinta ni avisa.
 *
 * `emailOn(item)` es el rótulo «Correo» de la fila tal como lo ve quien mira (`customEffects` /
 * `systemEffects`): así el filtro, el grupo y la fila no pueden decir dos cosas distintas.
 */
export function ruleUse(item, emailOn = defaultEmailOn) {
    if (!isActiveRule(item)) return USE_PAUSED;
    if (!isCustom(item) && item.notifies !== true) return USE_PAINT;
    return emailOn(item) ? USE_EMAIL : USE_NOTICE;
}

const FILTER_USE = {
    [FILTER_EMAIL]: USE_EMAIL,
    [FILTER_NOTICE]: USE_NOTICE,
    [FILTER_PAINT]: USE_PAINT,
    [FILTER_PAUSED]: USE_PAUSED,
};

function createdBy(item, me) {
    return isCustom(item) && me !== null && me !== undefined
        && Boolean(item.created_by) && String(item.created_by.id) === String(me);
}

/** `usageOf(item)`: el uso de la regla (`ruleUse` con el «Correo» de quien mira). */
export function matchesFilter(item, filter, me, usageOf = ruleUse) {
    if (filter === FILTER_MINE) return createdBy(item, me);
    const use = FILTER_USE[filter];
    return use ? usageOf(item) === use : true;
}

/**
 * Lo que la lista separa: las reglas personalizadas, las de Dashbo que pueden alertar y las que no
 * pueden (sin período de comparación), que van aparte con su motivo y no entran en los filtros.
 */
export function splitRules(payload) {
    // Una de Dashbo convertida (v2) no es una propia: en esta lista (la de una agencia sin convertir)
    // no debería llegar ninguna, y si llega no se dibuja como si la hubiera creado alguien.
    const rules = (payload && Array.isArray(payload.rules) ? payload.rules : [])
        .filter((rule) => !rule.deleted_on && rule.kind !== KIND_SYSTEM)
        .map((rule) => ({...rule, kind: KIND_CUSTOM}));
    const system = (payload && Array.isArray(payload.system_rules) ? payload.system_rules : [])
        .map((rule) => ({...rule, kind: KIND_SYSTEM}));
    return {
        custom: rules,
        system: system.filter((rule) => rule.can_alert !== false),
        cannotAlert: system.filter((rule) => rule.can_alert === false),
    };
}

export function normalizeSearch(text) {
    return String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Las filas que se ven y los números de cada filtro. Los contadores miran el buscador (cuentan lo
 * que el filtro mostraría con esa búsqueda) y nunca otro filtro.
 *
 * `searchTextOf(item)`: el texto contra el que busca (el título legible de cada regla, que arma
 * quien llama con i18n). `usageOf(item)`: qué hace la regla para quien mira (`ruleUse`).
 */
export function rulesView({custom, system}, {filter = FILTER_ALL, query = '', me = null, searchTextOf,
                                              usageOf = ruleUse}) {
    const needle = normalizeSearch(query);
    const matchesQuery = (item) => !needle || normalizeSearch(searchTextOf(item)).includes(needle);
    const searched = {custom: custom.filter(matchesQuery), system: system.filter(matchesQuery)};
    const matches = (key) => (item) => matchesFilter(item, key, me, usageOf);
    const counts = {};
    RULE_FILTERS.forEach((key) => {
        counts[key] = searched.custom.filter(matches(key)).length + searched.system.filter(matches(key)).length;
    });
    return {
        custom: searched.custom.filter(matches(filter)),
        system: searched.system.filter(matches(filter)),
        counts,
    };
}

// El id del DOM de una fila (para el deep link y el resaltado): `system:<IND>` no es un id válido.
export function ruleAnchorId(id) {
    return `alc-rule-${String(id).replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

// El deep link `?regla=`: `system:<indicador>` o el id de una regla personalizada.
export function matchesRuleParam(item, param) {
    if (!param || !item) return false;
    return String(item.id) === String(param);
}

// ------------------------------------------------------ v2: «Mis alertas» y «Avisos de control» ---
// Con `system_alerts_converted: true` (contrato v2 §3) las de Dashbo son filas de `AlertRule` como las
// propias: las que tienen número van en `rules` («Mis alertas») y las que son estados en `controls`
// («Avisos de control»). Con `false` (o un backend viejo, sin la clave) la pantalla es la de siempre.

/** La regla que devuelve una escritura: `{rule}` o la regla suelta (según el endpoint). `null` si no vino. */
export function ruleFromResponse(response) {
    const result = response && response.result;
    if (!result) return null;
    if (result.rule) return result.rule;
    return result.id !== undefined ? result : null;
}

export function isConverted(payload) {
    return Boolean(payload) && payload.system_alerts_converted === true;
}

export function isSystemRule(rule) {
    return Boolean(rule) && rule.kind === KIND_SYSTEM;
}

export function isControlRule(rule) {
    return Boolean(rule && rule.condition) && rule.condition.type === CONDITION_CONTROL;
}

export function ruleChannels(rule) {
    const channels = rule && rule.delivery && rule.delivery.channels;
    return Array.isArray(channels) ? channels : [];
}

/**
 * Los canales que tiene ese aviso (contrato v2 §2): casi todos correo y webhook; el saldo sólo webhook e «inversión
 * sin control» sólo correo. El interruptor que no está no se dibuja. Sin la clave (un backend viejo), los dos.
 */
export function availableChannels(rule) {
    const available = rule && rule.delivery && rule.delivery.available_channels;
    return Array.isArray(available) ? available : [CHANNEL_EMAIL, CHANNEL_WEBHOOK];
}

/** Una fila sin correo ni webhook no le avisa a nadie: se ve «Apagada» (no se borra ni se pausa). */
export function hasChannels(rule) {
    return ruleChannels(rule).length > 0;
}

const withKind = (rule) => ({...rule, kind: rule.kind === KIND_SYSTEM ? KIND_SYSTEM : KIND_CUSTOM});

/**
 * `{alerts, controls, suggestions}` del payload convertido. `alerts` respeta el orden del backend (las de
 * Dashbo prendidas, las propias, las de Dashbo sin canales): un cambio en la pantalla no mueve la fila
 * debajo del cursor. Un aviso de control que llegara en `rules` va igual con los controles.
 */
export function partition(payload) {
    const live = (list) => (Array.isArray(list) ? list : []).filter((rule) => rule && !rule.deleted_on);
    const rules = live(payload && payload.rules).map(withKind);
    return {
        alerts: rules.filter((rule) => !isControlRule(rule)),
        controls: [...live(payload && payload.controls).map((rule) => ({...rule, kind: KIND_SYSTEM})),
            ...rules.filter(isControlRule)],
        suggestions: Array.isArray(payload && payload.suggestions) ? payload.suggestions : [],
    };
}

/** Los avisos de control en sus dos grupos: «Prendidos» (con algún canal) y «Apagados». */
export function splitControls(controls) {
    return {on: controls.filter(hasChannels), off: controls.filter((rule) => !hasChannels(rule))};
}

/** La sugerida que corresponde a una clave (`?regla=` puede apuntar a una que todavía no es fila). */
export function suggestionByKey(suggestions, systemKey) {
    return (suggestions || []).find((item) => item.system_key === systemKey) || null;
}

/** La fila recién creada desde una sugerida: después de las que avisan, antes de las apagadas. */
export function insertAlertIn(payload, rule) {
    if (!payload || !rule) return payload;
    const rest = (payload.rules || []).filter((item) => String(item.id) !== String(rule.id));
    const lastOn = rest.reduce((found, item, index) => (hasChannels(item) ? index : found), -1);
    const rules = [...rest.slice(0, lastOn + 1), rule, ...rest.slice(lastOn + 1)];
    return {...payload, rules};
}

export function withoutSuggestion(payload, systemKey) {
    if (!payload || !Array.isArray(payload.suggestions)) return payload;
    return {...payload, suggestions: payload.suggestions.filter((item) => item.system_key !== systemKey)};
}

/**
 * La baja personal de quien mira, para pintarla antes de que conteste el backend: «A ti no te llega» con
 * la baja, y «Te llega a ti» al volver (si es de las que le llegaban; si no, el backend lo corrige).
 */
export function withViewerOptOut(rule, optedOut) {
    const delivery = (rule && rule.delivery) || {};
    return {...rule, delivery: {...delivery, viewer_opted_out: optedOut, reaches_viewer: !optedOut}};
}

/**
 * El borrador del editor después de «Dejar de recibirla» / «Volver a recibirla» (review C-ud18-fe-01). La baja propia y el
 * «salvo …» son el mismo dato en el backend: si el borrador conoce las bajas, suma o saca a quien mira y deja lo demás
 * como el usuario lo tenía. Sin esto, el pie decía «1 cambio sin guardar» y guardar le devolvía el correo.
 */
export function draftWithViewerExcluded(draft, viewerId, excluded) {
    const recipients = draft && draft.recipients;
    const id = Number(viewerId);
    if (!recipients || !recipients.excludedKnown || viewerId === null || viewerId === undefined || !Number.isFinite(id)) {
        return draft;
    }
    const rest = (recipients.excludedUserIds || []).filter((item) => Number(item) !== id);
    return {...draft, recipients: {...recipients, excludedUserIds: excluded ? [...rest, id] : rest}};
}

export function withChannels(rule, channels) {
    const delivery = (rule && rule.delivery) || {};
    return {...rule, delivery: {...delivery, channels: [...channels]}};
}

export function withRecipients(rule, type, userIds = []) {
    const delivery = (rule && rule.delivery) || {};
    const recipients = delivery.recipients || {};
    return {...rule, delivery: {...delivery, recipients: {...recipients, type, user_ids: [...userIds], resolved: recipients.resolved || []}}};
}

// La clave i18n de cada destinatario en la meta de la fila («Para el equipo de cada cliente ▾»).
const PARA_KEYS = {
    [RECIPIENTS_ASSIGNED]: 'alerts_center.row.para_assigned',
    [RECIPIENTS_ALL]: 'alerts_center.row.para_all',
    [RECIPIENTS_ADMINS]: 'alerts_center.row.para_admins',
};

/**
 * El texto del chip «Para». «A mí» se dice desde quien mira: «ti» si es quien la creó, su nombre si no.
 * «A estas personas», con cuántas son.
 */
export function paraText(rule, {t, viewerId = null}) {
    const recipients = (rule && rule.delivery && rule.delivery.recipients) || {};
    if (PARA_KEYS[recipients.type]) return t(PARA_KEYS[recipients.type]);
    if (recipients.type === RECIPIENTS_USERS) {
        // «Para: Kanka» (ronda 5, D17): las personas por su nombre, con lo que el backend resuelve.
        const ids = recipients.user_ids || [];
        const resolved = Array.isArray(recipients.resolved) ? recipients.resolved : [];
        // Quien mira es «ti», y va primero: «Para ti y Kanka» (UD-18).
        const isViewer = (user) => viewerId !== null && viewerId !== undefined && String(user.id) === String(viewerId);
        const names = [...resolved.filter(isViewer).map(() => t('alerts_center.row.para_me')),
            ...resolved.filter((user) => !isViewer(user)).map((user) => user.name)].filter(Boolean);
        if (names.length) return namesText(names, t, {more: Math.max(0, ids.length - names.length)});
        return t('alerts_center.row.para_users', {count: ids.length});
    }
    if (recipients.type === RECIPIENTS_ME) {
        const creator = createdByOf(rule);
        if (creator.kind === 'USER' && creator.name && String(creator.id) !== String(viewerId)) return creator.name;
        return t('alerts_center.row.para_me');
    }
    return t('alerts_center.row.para_assigned');
}

/**
 * Los destinatarios que ofrece el chip «Para» de una fila. Las de Dashbo no tienen «a mí» (no hay creador); en
 * una propia, «a mí» es SU creador (el backend resuelve `ME` así), así que se ofrece sólo a quien la creó: a otro
 * le cambiaría el destinatario a alguien que no es él (review C-av2-fe-centro-08). «Los administradores» va
 * siempre: en una agencia convertida el backend de v2 ya está, y lo acepta.
 */
export function paraChoices(rule, {viewerId = null} = {}) {
    if (isSystemRule(rule)) return [RECIPIENTS_ASSIGNED, RECIPIENTS_ALL, RECIPIENTS_ADMINS];
    const creator = createdByOf(rule);
    const mine = creator.kind === 'USER' && viewerId !== null && String(creator.id) === String(viewerId);
    const choices = [RECIPIENTS_ASSIGNED, RECIPIENTS_ALL, RECIPIENTS_ADMINS, RECIPIENTS_USERS];
    return mine ? [RECIPIENTS_ME, ...choices] : choices;
}

/** `{kind: 'DASHBO'}` o `{kind: 'USER', id, name}`; el backend viejo mandaba `{id, name}` (contrato v2 §2). */
export function createdByOf(rule) {
    const createdBy = rule && rule.created_by;
    if (!createdBy) return {kind: isSystemRule(rule) ? 'DASHBO' : 'USER', id: null, name: null};
    if (createdBy.kind === 'DASHBO') return {kind: 'DASHBO', id: null, name: null};
    return {kind: 'USER', id: createdBy.id === undefined ? null : createdBy.id, name: createdBy.name || null};
}

/** ¿Le llega a quien mira? Sólo cuenta el correo: el webhook va al canal de la agencia, no a una persona. */
export function reachesViewerByEmail(rule) {
    return ruleChannels(rule).includes(CHANNEL_EMAIL) && Boolean(rule.delivery && rule.delivery.reaches_viewer);
}

// ------------------------------------------------- ronda 4: según quién mira (README v2 §4.7) ----

export const AUDIENCE_ADMIN = 'admin';
export const AUDIENCE_MEMBER = 'member';
export const AUDIENCE_LEGACY = 'legacy';

/**
 * Quién mira (`result.viewer` del GET, D12): `admin` («Alertas de la agencia», «Le llega a N de M», «Ver como») o
 * `member` («Mis alertas»: lo que le llega y lo suyo). Sin `viewer` (un backend anterior a la ronda 4), `legacy`: la
 * lista de antes, para todos igual.
 */
export function audienceOf(viewer) {
    if (!viewer || typeof viewer.is_admin !== 'boolean') return AUDIENCE_LEGACY;
    return viewer.is_admin ? AUDIENCE_ADMIN : AUDIENCE_MEMBER;
}

/** ¿La creó quien mira? */
export function isOwnRule(rule, viewerId) {
    if (viewerId === null || viewerId === undefined) return false;
    const creator = createdByOf(rule);
    return creator.kind === 'USER' && String(creator.id) === String(viewerId);
}

/**
 * «Le llega a 3 de 5» (sólo para el admin, `delivery.recipients_summary`): a quiénes del equipo les llega el correo y
 * quiénes se dieron de baja. `null` si el backend no lo manda (un no admin, o un backend anterior).
 */
export function recipientsSummaryOf(rule) {
    const summary = rule && rule.delivery && rule.delivery.recipients_summary;
    if (!summary || typeof summary.receiving_count !== 'number') return null;
    return {
        receivingCount: summary.receiving_count,
        teamCount: typeof summary.team_count === 'number' ? summary.team_count : null,
        receiving: Array.isArray(summary.receiving) ? summary.receiving : [],
        optedOut: Array.isArray(summary.opted_out) ? summary.opted_out : [],
        // UD-18: a quién le llegaría aunque esté pausada (clave aditiva del backend). `null` si no viene.
        wouldReceive: Array.isArray(summary.would_receive) ? summary.would_receive : null,
    };
}

/** Un no admin crea sólo «a mí» (D13): el borrador va siempre con ese destinatario. */
export function withOnlyMe(draft) {
    if (!draft || (draft.recipients && draft.recipients.type === RECIPIENTS_ME)) return draft;
    return {...draft, recipients: {type: RECIPIENTS_ME, userIds: []}};
}

// ------------------------------------------------- ronda 5: la fila (UD-11, README v2 §4.8) ------------

/** ¿Es una regla de equipo? `delivery.recipients.is_team` si el backend lo manda (R); si no, por el tipo. */
export function isTeamRule(rule) {
    const recipients = rule && rule.delivery && rule.delivery.recipients;
    if (!recipients) return false;
    if (typeof recipients.is_team === 'boolean') return recipients.is_team;
    return TEAM_RECIPIENT_TYPES.includes(recipients.type);
}

/** Nombres de corrido: «Ana», «Ana y Juan», «Ana, Juan y Mora», «Ana, Juan, Mora y 2 más»; `more` suma los sin nombre. */
export function namesText(names, t, {more = 0, max = 3} = {}) {
    const shown = names.slice(0, max);
    const rest = names.length - shown.length + more;
    if (!shown.length) return rest > 0 ? t('alerts_center.row.names_count', {count: rest}) : '';
    if (rest > 0) {
        return t('alerts_center.row.names_more', {list: shown.join(', '), count: rest, interpolation: {escapeValue: false}});
    }
    if (shown.length === 1) return shown[0];
    return t('alerts_center.row.names_last', {list: shown.slice(0, -1).join(', '), last: shown[shown.length - 1],
        interpolation: {escapeValue: false}});
}

/**
 * Los nombres de las bajas de una regla de equipo («salvo …»), dichos igual en la fila, el editor y «Avisos de las
 * cuentas»: quien mira es «tú» y va primero, como en el «Para» (UD-18). `byId`: id → nombre. Los ids sin nombre se
 * cuentan aparte (`unknown`).
 */
export function exceptNames(ids, byId, {t, viewerId = null}) {
    const isViewer = (id) => viewerId !== null && viewerId !== undefined && String(id) === String(viewerId);
    const you = t('alerts_center.row.except_you');
    const names = [];
    let unknown = 0;
    [...ids.filter(isViewer), ...ids.filter((id) => !isViewer(id))].forEach((id) => {
        if (isViewer(id)) names.push(you);
        else if (byId.has(String(id))) names.push(byId.get(String(id)));
        else unknown += 1;
    });
    return {names, unknown};
}

/**
 * «Para: todo el equipo de cada cliente, salvo Ana y Juan» (ronda 5, D17/D19). Las bajas de una regla de equipo:
 * - el admin las recibe con nombre (`recipients.excluded`); sin nombres, los ids y los nombres de `team`;
 * - a un no admin no le llegan: sólo sabe la suya (`viewer_opted_out`), y lo dice «salvo tú». Con el filtro
 *   «Persona» ese flag es de otra persona, así que no se usa (`person`).
 *
 * Con la lista más corta (la madre, ronda 5): si a la regla la reciben menos personas que las que quedan afuera
 * (`recipients_summary`, sólo el admin), se nombra a quienes la reciben: «Para: Kanka», «ti y Kanka» (UD-18, el
 * mockup del 24/10, sin el «· en sus clientes» de la ronda 5: el alcance lo dice el grupo y el editor). Es lo que
 * deja la conversión cuando adentro hay un admin con cartera parcial.
 */
export function paraLine(rule, {t, viewerId = null, team = null, person = null}) {
    const who = paraText(rule, {t, viewerId});
    if (!isTeamRule(rule)) return who;
    const delivery = rule.delivery || {};
    const recipients = delivery.recipients || {};
    const you = t('alerts_center.row.except_you');
    const isViewer = (id) => viewerId !== null && viewerId !== undefined && String(id) === String(viewerId);
    const receiving = receivingLine(rule, {t, isViewer});
    if (receiving) return receiving;
    let names = [];
    let unknown = 0;
    if (Array.isArray(recipients.excluded) && recipients.excluded.length) {
        const byId = new Map(recipients.excluded.filter((user) => user.name).map((user) => [String(user.id), user.name]));
        ({names, unknown} = exceptNames(recipients.excluded.map((user) => user.id), byId, {t, viewerId}));
    } else if (Array.isArray(recipients.excluded_user_ids) && recipients.excluded_user_ids.length) {
        const byId = new Map((team || []).map((user) => [String(user.id), user.name || user.email]));
        ({names, unknown} = exceptNames(recipients.excluded_user_ids, byId, {t, viewerId}));
    } else if (!person && delivery.viewer_opted_out === true) {
        names = [you];
    }
    if (!names.length && !unknown) return who;
    return t('alerts_center.row.para_except', {who, except: namesText(names, t, {more: unknown}),
        interpolation: {escapeValue: false}});
}

function excludedCountOf(recipients) {
    if (Array.isArray(recipients.excluded) && recipients.excluded.length) return recipients.excluded.length;
    return Array.isArray(recipients.excluded_user_ids) ? recipients.excluded_user_ids.length : 0;
}

/**
 * «Kanka», «ti y Kanka» si la reciben menos de las que quedan afuera; `null` si no (o sin el resumen).
 *
 * UD-18: el «Para» se escribe igual en las pausadas. Con `would_receive` (a quién le llegaría, aunque la fila no mande
 * nada hoy) sale de ahí; sin esa clave, de `receiving`, que el backend manda vacío en una pausada: ahí la fila cae al
 * «salvo …» (P-1 de la tanda, pedido al backend).
 */
function receivingLine(rule, {t, isViewer}) {
    const delivery = rule.delivery || {};
    const recipients = delivery.recipients || {};
    const summary = recipientsSummaryOf(rule);
    const excluded = excludedCountOf(recipients);
    if (!summary) return null;
    const people = summary.wouldReceive !== null ? summary.wouldReceive : summary.receiving;
    const count = summary.wouldReceive !== null ? summary.wouldReceive.length : summary.receivingCount;
    if (count < 1 || !excluded || count >= excluded) return null;
    const me = t('alerts_center.row.para_me');
    // Quien mira primero: «ti y Kanka».
    const ordered = [...people.filter((user) => isViewer(user.id)), ...people.filter((user) => !isViewer(user.id))];
    const names = ordered.map((user) => (isViewer(user.id) ? me : user.name)).filter(Boolean);
    const list = namesText(names, t, {more: Math.max(0, count - names.length)});
    return list || null;
}

/** «Por correo», «Por correo y webhook» (ronda 5, D18: los canales se leen, no se prenden en la fila). */
export function channelsLine(rule, t) {
    const channels = ruleChannels(rule);
    const email = channels.includes(CHANNEL_EMAIL);
    const webhook = channels.includes(CHANNEL_WEBHOOK);
    if (email && webhook) return t('alerts_center.row.by_email_webhook');
    if (email) return t('alerts_center.row.by_email');
    if (webhook) return t('alerts_center.row.by_webhook');
    return t('alerts_center.row.by_none');
}

/**
 * La baja propia de la fila (ronda 5, D19): sólo en las reglas de equipo, prendidas y con correo. «Dejar de
 * recibirla» si hoy le llega; «Volver a recibirla» si se dio de baja.
 */
export function teamActionOf(rule) {
    if (!isTeamRule(rule) || !isActiveRule(rule) || !ruleChannels(rule).includes(CHANNEL_EMAIL)) return null;
    const delivery = rule.delivery || {};
    if (delivery.viewer_opted_out === true) return {key: 'opted_out', actionKey: 'alerts_center.row.receive_again', next: false};
    if (delivery.reaches_viewer === true) return {key: 'reaches', actionKey: 'alerts_center.row.stop_receiving', next: true};
    return null;
}

/** ¿Avisa? El switch de la fila (D18): prendida y con algún canal. */
export function isRuleOn(rule) {
    return isActiveRule(rule) && hasChannels(rule);
}

/** El borrador del correo siempre (D18): una agencia convertida no tiene alertas sin correo. */
export function withEmailAlways(draft) {
    if (!draft || (draft.channels || []).includes(CHANNEL_EMAIL)) return draft;
    return {...draft, channels: [CHANNEL_EMAIL, ...(draft.channels || []).filter((channel) => channel === CHANNEL_WEBHOOK)]};
}

/**
 * La familia de una clave de sistema: una hermana para unas líneas (`BUDGET_1@90`, la misma alerta con otro número)
 * es de la familia `BUDGET_1` (`system_catalog.family_of` del backend).
 */
export function systemFamily(systemKey) {
    return String(systemKey || '').split('@')[0];
}

/** ¿Es una de Dashbo para unas líneas (una hermana) y no para toda la agencia? */
export function isLineScopedSystem(rule) {
    return isSystemRule(rule) && Boolean(rule.scope) && Boolean(rule.scope.type) && rule.scope.type !== SCOPE_AGENCY;
}

// ------------------------------------------------- UD-18: «Mis reglas» por alcance (24/10) -------------
// La lista son tus alertas, agrupadas por dónde avisan; la configuración propia de una línea (una hermana de Dashbo)
// cuelga de su alerta como «Excepción», y los avisos de control tienen su propia página.

export const GROUP_AGENCY = 'agency';
export const GROUP_CLIENT = 'client';
export const GROUP_LINE = 'line';
export const RULE_GROUPS = [GROUP_AGENCY, GROUP_CLIENT, GROUP_LINE];

/** ¿Es una excepción? Una de Dashbo con `@` en la clave (`BUDGET_1@90`, también `@off`): una hermana de línea. */
export function isExceptionRule(rule) {
    return isSystemRule(rule) && String(rule.system_key || '').includes('@');
}

/**
 * El grupo de una alerta por su alcance: toda la agencia (o cualquier línea), un cliente, una línea. Las de una
 * etiqueta van con las de línea: avisan en unas líneas puntuales, no en toda la agencia.
 */
export function scopeGroupOf(rule) {
    const type = rule && rule.scope && rule.scope.type;
    if (type === SCOPE_CLIENT) return GROUP_CLIENT;
    if (type === SCOPE_LINE || type === SCOPE_LABEL) return GROUP_LINE;
    return GROUP_AGENCY;
}

/**
 * Las alertas en sus grupos, en el orden del backend, con las excepciones colgadas de su familia:
 * `[{key, entries: [{rule, exceptions: [rule], orphan}]}]`, sin los grupos vacíos.
 *
 * Una excepción cuya familia no tiene fila en la lista no debería existir (la conversión crea la de la agencia antes
 * que sus hermanas). Si pasa, no se esconde: va sola al grupo «Una línea», con `orphan` (la fila se titula por su
 * familia y dice en qué línea).
 */
export function groupAlerts(alerts) {
    const groups = {[GROUP_AGENCY]: [], [GROUP_CLIENT]: [], [GROUP_LINE]: []};
    const families = new Map();
    const list = Array.isArray(alerts) ? alerts : [];
    list.filter((rule) => !isExceptionRule(rule)).forEach((rule) => {
        const entry = {rule, exceptions: [], orphan: false};
        groups[scopeGroupOf(rule)].push(entry);
        if (isSystemRule(rule) && rule.system_key && !families.has(rule.system_key)) families.set(rule.system_key, entry);
    });
    list.filter(isExceptionRule).forEach((rule) => {
        const parent = families.get(systemFamily(rule.system_key));
        if (parent) parent.exceptions.push(rule);
        else groups[GROUP_LINE].push({rule, exceptions: [], orphan: true});
    });
    return RULE_GROUPS.map((key) => ({key, entries: groups[key]})).filter((group) => group.entries.length > 0);
}

/** Todas las filas de los grupos (las de la agencia y sus excepciones), para contar. */
export function groupRows(groups) {
    return groups.flatMap((group) => group.entries.flatMap((entry) => [entry.rule, ...entry.exceptions]));
}

/**
 * Lo que queda a la vista con un filtro (`test` sobre una fila): por grupo, cada fila con sus excepciones que pasan.
 * Una alerta que no pasa pero tiene una excepción que sí queda como contexto (`context`, atenuada): la excepción no se
 * entiende sin su alerta.
 */
export function filterGroups(groups, test) {
    return groups.map((group) => ({
        ...group,
        entries: group.entries.map((entry) => {
            const exceptions = entry.exceptions.filter(test);
            const own = test(entry.rule);
            if (!own && exceptions.length === 0) return null;
            return {...entry, exceptions, context: !own};
        }).filter(Boolean),
    })).filter((group) => group.entries.length > 0);
}

// El filtro de estado de la lista: el switch de la fila (`isRuleOn`).
export const STATUS_ALL = 'all';
export const STATUS_ACTIVE = 'active';
export const STATUS_PAUSED = 'paused';
export const STATUS_FILTERS = [STATUS_ALL, STATUS_ACTIVE, STATUS_PAUSED];

export function matchesStatus(rule, status) {
    if (status === STATUS_ACTIVE) return isRuleOn(rule);
    if (status === STATUS_PAUSED) return !isRuleOn(rule);
    return true;
}

/**
 * Los clientes en los que avisa una alerta, como ids en texto, o `null` si avisa en cualquiera (toda la agencia,
 * cualquier línea, una etiqueta: no se sabe de qué clientes son sus líneas). Las líneas y los clientes que hoy no se
 * miden cuentan si el backend los nombra (`scope.inactive`, P-2 de la tanda).
 */
export function ruleClientIds(rule) {
    const scope = (rule && rule.scope) || {};
    const inactive = Array.isArray(scope.inactive) ? scope.inactive : [];
    const clean = (ids) => new Set(ids.filter((id) => id !== null && id !== undefined).map(String));
    if (scope.type === SCOPE_CLIENT) {
        return clean([...(scope.ids || []), ...inactive.map((item) => (item.client_id !== undefined ? item.client_id : item.id))]);
    }
    if (scope.type === SCOPE_LINE) {
        const resolved = Array.isArray(scope.resolved) ? scope.resolved : [];
        return clean([...resolved, ...inactive].map((item) => item.client_id));
    }
    return null;
}

/** El filtro «Cliente»: las de toda la agencia se ven con cualquier cliente; una de un cliente o de una línea, con el suyo. */
export function matchesClient(rule, clientId) {
    if (clientId === null || clientId === undefined || clientId === '') return true;
    const ids = ruleClientIds(rule);
    return ids === null || ids.has(String(clientId));
}

/**
 * Los clientes que ofrece el filtro «Cliente»: los de `options.clients` (los que quien mira ve) o, sin `options`, los
 * que nombran las alertas. Por nombre.
 */
export function clientChoices(alerts, options) {
    const byId = new Map();
    if (options && Array.isArray(options.clients)) {
        options.clients.forEach((client) => byId.set(String(client.id), client.name));
    } else {
        (alerts || []).forEach((rule) => {
            const scope = (rule && rule.scope) || {};
            const resolved = [...(Array.isArray(scope.resolved) ? scope.resolved : []),
                ...(Array.isArray(scope.inactive) ? scope.inactive : [])];
            resolved.forEach((item) => {
                if (scope.type === SCOPE_CLIENT && item.id !== undefined) byId.set(String(item.id), item.name);
                if (scope.type === SCOPE_LINE && item.client_id !== undefined) byId.set(String(item.client_id), item.client_name);
            });
        });
    }
    return [...byId.entries()].filter(([, name]) => Boolean(name))
        .map(([id, name]) => ({id, name}))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Los avisos de las cuentas, para la franja de «Mis reglas»: cuántos hay, cuántos avisan (el switch) y cuántos le
 * llegan por correo a quien mira (o a la persona del filtro, que es de quien viene `reaches_viewer`).
 */
export function controlsSummary(controls) {
    const list = Array.isArray(controls) ? controls : [];
    // Uno pausado no le llega a nadie, diga lo que diga `reaches_viewer` (el backend ya lo manda en false).
    return {
        total: list.length,
        on: list.filter(isRuleOn).length,
        mine: list.filter((rule) => isRuleOn(rule) && reachesViewerByEmail(rule)).length,
    };
}

// Los tipos de las sugeridas en «Crear una alerta» (UD-18): ritmo y gasto, resultados y las comparativas del Tablero.
export const SUGGESTION_PACE = 'pace';
export const SUGGESTION_RESULTS = 'results';
export const SUGGESTION_BOARD = 'board';
export const SUGGESTION_KINDS = [SUGGESTION_PACE, SUGGESTION_RESULTS, SUGGESTION_BOARD];
const RESULT_FAMILIES = ['GOALS_1', 'GOALS_2', 'ROAS_VS_LAST_MONTH', 'ACOS_VS_LAST_MONTH'];

export function suggestionKindOf(systemKey) {
    const key = String(systemKey || '');
    if (key.startsWith('FLEX:')) return SUGGESTION_BOARD;
    return RESULT_FAMILIES.includes(systemFamily(key)) ? SUGGESTION_RESULTS : SUGGESTION_PACE;
}

/**
 * Qué cambió en el editor contra la regla guardada, una clave por cosa que el usuario reconoce (el número, a quién, por
 * dónde…): «2 cambios sin guardar». Sale del mismo PUT que se mandaría (`ruleUpdateFromDraft`), así nunca dice «sin
 * cambios» con algo para guardar.
 */
export function draftChanges(rule, draft, {locale = 'es'} = {}) {
    if (!rule || !draft) return [];
    const update = ruleUpdateFromDraft(rule, draft, {locale});
    const before = draftFromRule(rule, {locale});
    const changes = [];
    if (update.name !== undefined) changes.push('name');
    if (update.condition) {
        ['metric', 'operator', 'period', 'vsPeriod'].forEach((field) => {
            if ((draft[field] || null) !== (before[field] || null)) changes.push(field);
        });
        if (parseValue(draft.value, locale) !== parseValue(before.value, locale)) changes.push('value');
    }
    if (update.scope) changes.push('scope');
    if (update.delivery) {
        const now = deliveryInput(draft);
        const prev = deliveryInput(before);
        if (!sameJson([...now.channels].sort(), [...prev.channels].sort())) changes.push('channels');
        if (now.recipients.type !== prev.recipients.type
            || !sameJson(sortedIds(now.recipients.user_ids), sortedIds(prev.recipients.user_ids))) changes.push('recipients');
        if (!sameJson(sortedIds(now.recipients.excluded_user_ids || []), sortedIds(prev.recipients.excluded_user_ids || []))) {
            changes.push('except');
        }
    }
    if (update.is_active !== undefined) changes.push('active');
    // Un bloque que viaja sin que se reconozca qué cambió (no debería pasar) cuenta igual: hay algo para guardar.
    if (!changes.length && Object.keys(update).length) changes.push('other');
    return changes;
}

// El indicador de la pantalla vieja de cada clave de sistema (`?regla=system:<IND>` de los correos y la
// tuerca viejos). El backend lo manda en `legacy_indicator` (una lista, en las SYSTEM y en las sugeridas: contrato
// v2 §2), que se lee primero; esta tabla es la red si no viene.
const LEGACY_INDICATORS_BY_KEY = {
    BUDGET_1: ['EXCEED_BUDGET_INDICATOR'],
    BUDGET_2: ['EXCEED_BUDGET_INDICATOR'],
    GOALS_1: ['GOAL_1_INDICATOR'],
    GOALS_2: ['GOAL_2_INDICATOR'],
    PACING: ['CURRENT_PERIOD_COST_VS_TIME_COMPLETION_INDICATOR'],
    ODS_YESTERDAY: ['ODS_VS_YESTERDAY_INDICATOR'],
    ODS_7D: ['ODS_VS_LAST_7_DAYS_INDICATOR'],
    ODS_TODAY: ['ODS_VS_TODAY_INDICATOR'],
    ROAS_VS_LAST_MONTH: ['CURRENT_ROAS_VS_LAST_MONTH'],
    ACOS_VS_LAST_MONTH: ['CURRENT_ACOS_VS_LAST_MONTH'],
    FUNDS: ['FUNDS_INDICATOR'],
    UNCOVERED_SPEND: ['UNCOVERED_SPEND_INDICATOR'],
    ODS_VS_PLATFORM: ['ODS_VS_PLATFORM_BUDGET_INDICATOR'],
};

function legacyIndicatorsOf(item) {
    const fromBackend = item && (item.legacy_indicator || (item.legacy && item.legacy.indicator));
    if (fromBackend) return Array.isArray(fromBackend) ? fromBackend : [fromBackend];
    return LEGACY_INDICATORS_BY_KEY[systemFamily(item && item.system_key)] || [];
}

/**
 * Lo que abre `?regla=` en la lista convertida (contrato v2 §8): `{kind: 'row', id}` (una fila, por id o
 * por su indicador viejo), `{kind: 'suggestion', key}` (una que todavía es sugerida) o `null`. De dos filas
 * con el mismo indicador (los dos umbrales de presupuesto), la primera.
 */
export function resolveRuleParam(param, {alerts = [], controls = [], suggestions = []}) {
    if (!param) return null;
    const rows = [...alerts, ...controls];
    const byId = rows.find((item) => matchesRuleParam(item, param));
    if (byId) return {kind: 'row', id: byId.id};
    const text = String(param);
    if (!text.startsWith('system:')) return null;
    const indicator = text.slice('system:'.length);
    // De varias (los dos umbrales, una hermana para unas líneas), la primera de toda la agencia
    // (review C-av2-fe-centro-02).
    const matching = rows.filter((item) => legacyIndicatorsOf(item).includes(indicator));
    const row = matching.find((item) => !isLineScopedSystem(item)) || matching[0];
    if (row) return {kind: 'row', id: row.id};
    const suggestion = suggestions.find((item) => legacyIndicatorsOf(item).includes(indicator)
        || (item.flex && item.flex.indicator === indicator));
    return suggestion ? {kind: 'suggestion', key: suggestion.system_key} : null;
}

// ------------------------------------------------------- cambios locales del payload --------------
// La lista se actualiza en el acto (optimista) y se corrige con lo que devuelve el backend. Las reglas
// personalizadas viven en `rules` y las de sistema en `system_rules`; los ids no se pisan (número vs
// `system:<IND>`).

// El tope cuenta las activas de TODA la agencia, y un usuario con clientes asignados ve sólo algunas:
// recontarlas con lo que se ve lo bajaba. Se ajusta el número del backend con lo que cambió
// (C-ac-fe-centro-22).
// El tope cuenta sólo las propias (contrato v2 §1): las de Dashbo no lo comen.
const countsAsActive = (rule) => Boolean(rule) && rule.kind !== KIND_SYSTEM && rule.is_active !== false && !rule.deleted_on;

function withLimits(payload, rules, delta) {
    if (!payload.limits || typeof payload.limits.active !== 'number' || !delta) return {...payload, rules};
    return {...payload, rules, limits: {...payload.limits, active: Math.max(0, payload.limits.active + delta)}};
}

export function replaceRuleIn(payload, rule) {
    if (!payload || !rule) return payload;
    const sameId = (item) => String(item.id) === String(rule.id);
    const previous = (payload.rules || []).find(sameId);
    if (previous) {
        const next = {...previous, ...rule};
        const delta = Number(countsAsActive(next)) - Number(countsAsActive(previous));
        return withLimits(payload, payload.rules.map((item) => (sameId(item) ? next : item)), delta);
    }
    if ((payload.system_rules || []).some(sameId)) {
        return {...payload, system_rules: payload.system_rules.map((item) => (sameId(item) ? {...item, ...rule} : item))};
    }
    if ((payload.controls || []).some(sameId)) {
        return {...payload, controls: payload.controls.map((item) => (sameId(item) ? {...item, ...rule} : item))};
    }
    return payload;
}

export function removeRuleFrom(payload, id) {
    if (!payload) return payload;
    const removed = (payload.rules || []).find((item) => String(item.id) === String(id));
    const rules = (payload.rules || []).filter((item) => String(item.id) !== String(id));
    const next = withLimits(payload, rules, removed && countsAsActive(removed) ? -1 : 0);
    // Los avisos de control viven aparte (v2): un 404 sobre uno también lo saca (review C-av2-fe-centro-20).
    if (!Array.isArray(payload.controls)) return next;
    return {...next, controls: payload.controls.filter((item) => String(item.id) !== String(id))};
}

/** Una regla que vuelve (deshacer) o que se acaba de crear: arriba de todo, sin duplicarla. */
export function insertRuleIn(payload, rule) {
    if (!payload || !rule) return payload;
    const previous = (payload.rules || []).find((item) => String(item.id) === String(rule.id));
    const rest = (payload.rules || []).filter((item) => String(item.id) !== String(rule.id));
    const delta = Number(countsAsActive(rule)) - Number(countsAsActive(previous));
    return withLimits(payload, [rule, ...rest], delta);
}

/** ¿Se puede crear (o reanudar) una regla más? `limits` ausente = no se sabe: no se bloquea. */
export function limitReached(limits) {
    // `max_active` es el nombre de hoy; el contrato v2 lo escribe `max`.
    const max = limits && (typeof limits.max_active === 'number' ? limits.max_active : limits.max);
    if (!limits || typeof max !== 'number' || typeof limits.active !== 'number') return false;
    return limits.active >= max;
}
